import type {
  StockAdjustmentInput,
  StockMovementDTO,
  StockMovementQueryInput,
  StockProductGroupDTO,
  StockSummaryDTO,
  StockThresholdUpdateInput,
  VariantWithStockDTO,
} from "@/modules/inventory/domain/contracts";
import type {
  CursorPageDTO,
  FilterConditionDTO,
  OffsetPageDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type {
  StockMovementDTO,
  StockProductGroupDTO,
  StockSummaryDTO,
  VariantWithStockDTO,
};

type I18nNameJson = { lo?: string; th?: string; en?: string };

export type StockFilterProductOption = {
  id: string;
  name: I18nNameJson;
  slug: string;
  values: Array<{ id: string; value: I18nNameJson }>;
};

function normalizeFilterConditions(
  filters: OffsetPageQueryDTO["filters"],
): FilterConditionDTO[] | undefined {
  if (filters == null) return undefined;
  if (Array.isArray(filters)) return filters;
  if (typeof filters === "string") {
    try {
      const parsed = JSON.parse(filters) as unknown;
      return Array.isArray(parsed)
        ? (parsed as FilterConditionDTO[])
        : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

export const inventoryApi = {
  stock: {
    async listFilterProducts(): Promise<
      Array<{ id: string; name: I18nNameJson }>
    > {
      return fetcher.get(`${config.apiUrl}/inventory/stock/filter-products`);
    },

    async listFilterProductOptions(
      productId: string,
    ): Promise<StockFilterProductOption[]> {
      return fetcher.get(
        `${config.apiUrl}/inventory/stock/filter-products/${encodeURIComponent(productId)}/options`,
      );
    },

    async summary(query: {
      filters?: OffsetPageQueryDTO["filters"];
    }): Promise<StockSummaryDTO> {
      const url = new URL(`${config.apiUrl}/inventory/stock/summary`);
      const filters = normalizeFilterConditions(query.filters);
      if (filters?.length)
        url.searchParams.set("filters", JSON.stringify(filters));
      return fetcher.get<StockSummaryDTO>(url.toString());
    },

    async listCursor(query: {
      limit?: number;
      after?: string;
      filters?: OffsetPageQueryDTO["filters"];
    }): Promise<CursorPageDTO<VariantWithStockDTO>> {
      const url = new URL(`${config.apiUrl}/inventory/stock/cursor`);
      url.searchParams.set("limit", String(query.limit ?? 40));
      if (query.after?.trim())
        url.searchParams.set("after", query.after.trim());
      const filters = normalizeFilterConditions(query.filters);
      if (filters?.length)
        url.searchParams.set("filters", JSON.stringify(filters));
      return fetcher.get<CursorPageDTO<VariantWithStockDTO>>(url.toString());
    },

    async listProductsCursor(query: {
      limit?: number;
      after?: string;
      filters?: OffsetPageQueryDTO["filters"];
    }): Promise<CursorPageDTO<StockProductGroupDTO>> {
      const url = new URL(`${config.apiUrl}/inventory/stock/products-cursor`);
      url.searchParams.set("limit", String(query.limit ?? 40));
      if (query.after?.trim())
        url.searchParams.set("after", query.after.trim());
      const filters = normalizeFilterConditions(query.filters);
      if (filters?.length)
        url.searchParams.set("filters", JSON.stringify(filters));
      return fetcher.get<CursorPageDTO<StockProductGroupDTO>>(url.toString());
    },

    async list(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<VariantWithStockDTO>> {
      const url = new URL(`${config.apiUrl}/inventory/stock`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      const filters = normalizeFilterConditions(query.filters);
      if (filters?.length)
        url.searchParams.set("filters", JSON.stringify(filters));
      return fetcher.get<OffsetPageDTO<VariantWithStockDTO>>(url.toString());
    },

    /**
     * ดึงรายการ stock ทุกแถวที่ตรง filter/sort ตาม `total` โดยแบ่งเรียก API เป็นหน้าๆ
     * (ใช้ตอน export เมื่อต้องการข้อมูลครบทุกหน้า ไม่ใช่แค่หน้าปัจจุบัน)
     */
    async listAllPages(
      query: Pick<OffsetPageQueryDTO, "sort" | "filters">,
      total: number,
    ): Promise<VariantWithStockDTO[]> {
      if (total <= 0) return [];
      const pageSize = 500;
      const rows: VariantWithStockDTO[] = [];
      for (let offset = 0; offset < total; offset += pageSize) {
        const res = await inventoryApi.stock.list({
          limit: Math.min(pageSize, total - offset),
          offset,
          sort: query.sort,
          filters: query.filters,
        });
        rows.push(...res.data);
      }
      return rows;
    },

    async get(variantId: string): Promise<VariantWithStockDTO> {
      return fetcher.get<VariantWithStockDTO>(
        `${config.apiUrl}/inventory/stock/${variantId}`,
      );
    },

    async adjust(input: StockAdjustmentInput): Promise<VariantWithStockDTO> {
      return fetcher.post<VariantWithStockDTO>(
        `${config.apiUrl}/inventory/stock/adjust`,
        input,
      );
    },

    async updateThreshold(
      variantId: string,
      input: StockThresholdUpdateInput,
    ): Promise<VariantWithStockDTO> {
      return fetcher.put<VariantWithStockDTO>(
        `${config.apiUrl}/inventory/stock/${variantId}/threshold`,
        input,
      );
    },
  },

  movements: {
    async list(query: StockMovementQueryInput): Promise<
      OffsetPageDTO<
        StockMovementDTO & {
          sku: string;
          productName: string | null;
          createdByName: string | null;
        }
      >
    > {
      const url = new URL(`${config.apiUrl}/inventory/movements`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      if (query.variantId) url.searchParams.set("variantId", query.variantId);
      if (query.type) url.searchParams.set("type", query.type);
      if (query.dateFrom) url.searchParams.set("dateFrom", query.dateFrom);
      if (query.dateTo) url.searchParams.set("dateTo", query.dateTo);
      return fetcher.get<
        OffsetPageDTO<
          StockMovementDTO & {
            sku: string;
            productName: string | null;
            createdByName: string | null;
          }
        >
      >(url.toString());
    },
  },
};
